/* ==========================================================
   DL v7 Sections — interactivity (v18)
   - Initial Picker: letter + metal + product CTA selection
     • LETTER-KEYED VARIANT IMAGE WINS over per-block override,
       so clicking a letter visibly updates the preview.
     • Per-block override is used only when no variant image
       exists for the current product × metal × letter.
     • Section-level default is the last fallback.
   - Stack Builder: custom-dropdown behavior, an optional per-slot
     "Add to basket" button (theme's native /cart/add.js +
     opencart/updateCart event convention, same pattern as
     sections/stack-builder.liquid), and an explicit variant picker:
     picking a dropdown option flagged data-has-letters="true" opens
     a 26-letter grid sub-panel, and one flagged data-has-lengths="true"
     (v12 — e.g. a chain sold in 40cm/45cm/50cm/60cm) opens a size list
     sub-panel — either way, instead of silently auto-selecting a
     variant, so shoppers explicitly choose for personalized/
     multi-length products.
     • v15: the merchant-configurable "mobile_picker_style" setting
       (bottom sheet / enlarged dropdown / in-place accordion) is
       driven almost entirely by CSS via [data-mobile-picker] on the
       section root; the JS only needs to manage the dim backdrop and
       background-scroll lock used by the bottom-sheet style
       (setSheetOpen — safe no-op for the other two styles).
     • v16 — FIX: the combined "Stack kaufen" CTA added the bundle to
       the real cart via AJAX, but never applied any actual Shopify
       discount, so the struck-through "was/now" price on the page was
       cosmetic only and checkout charged full price. Now, right after
       the /cart/add.js call succeeds, applyDiscountCode() applies the
       section's configured "discount_code" to the cart via
       /cart/update.js (the Cart AJAX API's documented way to set a
       cart-level discount code without a page reload) — so the
       discount shown is the discount charged. No-ops if no code is
       configured.
     • v16 also adds an equivalent "desktop_picker_style" setting
       ([data-desktop-picker] on the section root, mirrored in the CSS)
       so desktop is independently configurable instead of hardcoded —
       see dl-v7-sections.css.
     • v18 — FIX: shoppers had no explicit, always-visible way to
       collapse an open picker panel — it only closed implicitly (by
       picking an option or by tapping outside), which was easy to miss
       on the bottom-sheet style in particular since it covers most of
       the screen with no "X"/close affordance inside it. Every picker
       panel now renders a small header row (slot label + a ✕ button,
       see .dl-v7-stack__ddl-panel-head / .dl-v7-stack__ddl-close in
       the liquid + CSS) that explicitly closes just that slot's picker
       — wired up below via the [data-ddl-close] handler.
   ========================================================== */
(function() {
  'use strict';

  function formatMoney(cents, format) {
    format = format || '€{{amount_with_comma_separator}}';
    var value = cents / 100;
    function withCommas(num) {
      var parts = num.toFixed(2).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    function withSpaces(num) {
      var parts = num.toFixed(2).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    }
    function withCommaSep(num) {
      var parts = num.toFixed(2).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.join(',');
    }
    function withApostropheSep(num) {
      var parts = num.toFixed(2).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return parts.join('.');
    }
    function noDecimals(num) { return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
    function noDecimalsWithComma(num) { return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'); }
    function noDecimalsWithSpace(num) { return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '); }
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimalsWithComma(value))
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, noDecimalsWithSpace(value))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals(value))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommaSep(value))
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, withSpaces(value))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, withApostropheSep(value))
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas(value));
  }

  /* ===== INITIAL PICKER ===== */
  function initInitialPicker(root) {
    if (!root || root.dataset.dlv7Init === '1') return;
    root.dataset.dlv7Init = '1';

    var productMaps = {};
    try { productMaps = JSON.parse(root.dataset.productMaps || '{}'); } catch(e) {}

    var sectionDefaultImg = root.dataset.sectionDefaultImg || '';

    var chips = root.querySelectorAll('.dl-v7-initial__chip');
    var toggleBtns = root.querySelectorAll('.dl-v7-initial__toggle-btn');
    var previewImg = root.querySelector('.dl-v7-initial__preview img');
    var previewBox = root.querySelector('.dl-v7-initial__preview');
    var captionLetter = root.querySelector('.dl-v7-initial__caption-letter');
    var captionMetal = root.querySelector('.dl-v7-initial__caption-metal');
    var products = root.querySelectorAll('.dl-v7-initial__product');

    var state = {
      letter: root.dataset.defaultLetter || 'A',
      metal: root.dataset.defaultMetal || 'gold',
      productIdx: parseInt(root.dataset.defaultProductIdx || '0', 10)
    };

    function ensurePreviewImg() {
      if (previewImg || !previewBox) return;
      var img = document.createElement('img');
      img.alt = 'Initial preview';
      img.loading = 'lazy';
      img.width = 200; img.height = 200;
      previewBox.appendChild(img);
      previewImg = img;
    }

    function pickImage() {
      var pm = productMaps[state.productIdx] || productMaps[0] || {};
      var metalMap = pm[state.metal] || pm.gold || {};
      var entry = metalMap[state.letter];
      // 1) Letter-keyed variant image wins — so clicking a letter visibly
      //    changes the preview (even for CTAs that have an override image set).
      if (entry && entry.image) return entry.image;
      // 2) Per-block override (used when the product has no variant image for this letter).
      if (pm.__cta_override) return pm.__cta_override;
      // 3) Product featured fallback for active metal.
      if (metalMap.__fallback) return metalMap.__fallback;
      // 4) Section-level default override.
      return sectionDefaultImg || '';
    }

    function readMetalLabel(metalKey) {
      var btn = root.querySelector('.dl-v7-initial__toggle-btn[data-metal="' + metalKey + '"]');
      return btn ? btn.textContent.trim() : (metalKey.charAt(0).toUpperCase() + metalKey.slice(1));
    }

    function update() {
      if (captionLetter) captionLetter.textContent = state.letter;
      if (captionMetal) captionMetal.textContent = readMetalLabel(state.metal);

      ensurePreviewImg();
      var nextSrc = pickImage();
      if (previewImg && nextSrc) previewImg.src = nextSrc;

      products.forEach(function(p, idx) {
        var ppm = productMaps[idx] || {};
        var pMetalMap = ppm[state.metal] || ppm.gold || {};
        var letterEntry = pMetalMap[state.letter];
        var baseUrl = pMetalMap.__url || p.dataset.baseUrl || '#';
        if (letterEntry && letterEntry.variantId && baseUrl !== '#') {
          var sep = baseUrl.indexOf('?') >= 0 ? '&' : '?';
          p.setAttribute('href', baseUrl + sep + 'variant=' + letterEntry.variantId);
        } else {
          p.setAttribute('href', baseUrl);
        }
        if (idx === state.productIdx) p.classList.add('is-active');
        else p.classList.remove('is-active');
      });
    }

    chips.forEach(function(chip) {
      chip.addEventListener('click', function(e) {
        e.preventDefault();
        chips.forEach(function(c) { c.classList.remove('is-active'); });
        chip.classList.add('is-active');
        state.letter = chip.textContent.trim();
        update();
      });
    });

    toggleBtns.forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        toggleBtns.forEach(function(b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        state.metal = btn.dataset.metal || 'gold';
        update();
      });
    });

    products.forEach(function(p, idx) {
      p.addEventListener('click', function(e) {
        if (state.productIdx !== idx) {
          e.preventDefault();
          state.productIdx = idx;
          update();
        }
      });
    });

    update();
  }

  /* ===== Shared: add a single variant to cart via the theme's native
     AJAX convention, then hand off to the theme's own cart drawer. ===== */
  function addVariantToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function(res) {
      if (!res.ok) {
        return res.json().catch(function() { return {}; }).then(function(err) {
          throw new Error(err.description || err.message || ('HTTP ' + res.status));
        });
      }
      return res.json();
    }).then(function(data) {
      // Single event dispatch → theme refreshes drawer + any free-gift logic.
      document.dispatchEvent(new CustomEvent('opencart', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('updateCart', { bubbles: true }));
      return data;
    });
  }

  /* ===== STACK BUILDER (with custom dropdown) ===== */
  function initStackBuilder(root) {
    if (!root || root.dataset.dlv7Init === '1') return;
    root.dataset.dlv7Init = '1';

    var slots = root.querySelectorAll('.dl-v7-stack__slot');
    var priceEl = root.querySelector('.dl-v7-stack__price');
    var strikeEl = root.querySelector('.dl-v7-stack__strike');
    var ctaEl = root.querySelector('.dl-v7-stack__cta');
    var discountPct = parseFloat(root.dataset.discountPct || '15');
    var discountCode = (root.dataset.discountCode || '').trim();
    var currencyFormat = root.dataset.currencyFormat || '€{{amount_with_comma_separator}}';
    var ctaBaseUrl = root.dataset.ctaBaseUrl || '/cart';
    var mobilePickerStyle = root.dataset.mobilePicker || 'bottom_sheet';
    var backdrop = root.querySelector('.dl-v7-stack__backdrop');

    /* Applies the merchant-configured discount code to the cart — no
       manual code entry, no full-page checkout redirect. Called right
       after the combined CTA's /cart/add.js succeeds, so the "−X%
       Bundle" price shown on the page is what actually gets charged.
       Resolves even on failure (logs a warning) so a discount hiccup
       never blocks the add-to-cart flow that already succeeded. */
    function applyDiscountCode() {
      if (!discountCode) return Promise.resolve();
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: discountCode })
      }).then(function(res) {
        if (!res.ok) {
          console.error('[DL v7 Stack] discount code apply failed', res.status);
        }
        return res;
      }).catch(function(err) {
        console.error('[DL v7 Stack] discount code apply failed', err);
        return null;
      });
    }

    var allDropdowns = [];
    var allPanels = [];
    var ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    /* Bottom-sheet mode dims the page behind the sheet and locks
       background scroll while it's open; harmless no-op for the other
       two mobile picker styles (and for desktop, which never sets
       these classes to visible via CSS). */
    function setSheetOpen(isOpen) {
      if (backdrop) backdrop.classList.toggle('is-open', isOpen);
      if (mobilePickerStyle === 'bottom_sheet') {
        document.body.classList.toggle('dl-v7-scroll-lock', isOpen);
      }
    }

    function closeAllDropdowns(except) {
      allDropdowns.forEach(function(d) {
        if (d !== except) d.classList.remove('is-open');
      });
      // Always reset any open letter/size sub-panel back to the product
      // list, so a dropdown that's reopened later starts fresh.
      allPanels.forEach(function(p) {
        p.classList.remove('is-letter-mode');
        p.classList.remove('is-size-mode');
      });
      if (!except) setSheetOpen(false);
    }

    function closeVariantMode(panel) {
      if (!panel) return;
      panel.classList.remove('is-letter-mode');
      panel.classList.remove('is-size-mode');
    }

    /* Apply a concrete variant selection (either a plain product option,
       or a specific letter chosen inside the letter sub-panel) to a slot. */
    function applySelection(ddl, options, btnThumb, btnLabel, variantId, priceCents, image, label) {
      ddl.dataset.selectedVariant = variantId || '';
      ddl.dataset.selectedPriceCents = priceCents || '0';
      ddl.dataset.selectedImage = image || '';

      if (btnThumb && image) {
        btnThumb.style.backgroundImage = 'url(' + image + ')';
      }
      if (btnLabel) {
        btnLabel.textContent = label || '';
      }

      ddl.classList.remove('is-open');
      setSheetOpen(false);
      recalc();
    }

    /* Build and show the 26-letter grid for a personalized product option,
       instead of silently picking selected_or_first_available_variant. */
    function openLetterMode(ddl, panel, opt, options, btnThumb, btnLabel) {
      if (!panel) return;

      var lettersData = [];
      try { lettersData = JSON.parse(opt.dataset.letters || '[]'); } catch (e) {}
      var byLetter = {};
      lettersData.forEach(function(entry) {
        if (entry && entry.letter) byLetter[entry.letter] = entry;
      });

      var titleEl = panel.querySelector('[data-ddl-letters-title]');
      if (titleEl) titleEl.textContent = opt.dataset.name || 'Zurück';

      var grid = panel.querySelector('.dl-v7-stack__ddl-letters-grid');
      if (grid) {
        grid.innerHTML = '';
        ALPHABET.forEach(function(letter) {
          var entry = byLetter[letter];
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dl-v7-stack__ddl-letter';
          btn.textContent = letter;

          if (!entry || entry.available === false) {
            btn.disabled = true;
          } else {
            btn.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              options.forEach(function(o) { o.classList.remove('is-selected'); });
              opt.classList.add('is-selected');
              var productName = opt.dataset.name || '';
              var label = productName ? (productName + ' — ' + letter) : letter;
              applySelection(ddl, options, btnThumb, btnLabel, entry.variantId, entry.price, entry.image || opt.dataset.image, label);
              closeVariantMode(panel);
            });
          }
          grid.appendChild(btn);
        });
      }

      panel.classList.remove('is-size-mode');
      panel.classList.add('is-letter-mode');
    }

    /* Build and show the size list for a product sold across multiple
       lengths (e.g. a chain in 40cm/45cm/50cm/60cm), instead of silently
       picking selected_or_first_available_variant. */
    function openSizeMode(ddl, panel, opt, options, btnThumb, btnLabel) {
      if (!panel) return;

      var sizesData = [];
      try { sizesData = JSON.parse(opt.dataset.lengths || '[]'); } catch (e) {}

      var titleEl = panel.querySelector('[data-ddl-sizes-title]');
      if (titleEl) titleEl.textContent = opt.dataset.name || 'Zurück';

      var grid = panel.querySelector('.dl-v7-stack__ddl-sizes-grid');
      if (grid) {
        grid.innerHTML = '';
        sizesData.forEach(function(entry) {
          if (!entry || !entry.value) return;
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dl-v7-stack__ddl-size';
          btn.textContent = entry.value;

          if (entry.available === false) {
            btn.disabled = true;
          } else {
            btn.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              options.forEach(function(o) { o.classList.remove('is-selected'); });
              opt.classList.add('is-selected');
              var productName = opt.dataset.name || '';
              var label = productName ? (productName + ' — ' + entry.value) : entry.value;
              applySelection(ddl, options, btnThumb, btnLabel, entry.variantId, entry.price, entry.image || opt.dataset.image, label);
              closeVariantMode(panel);
            });
          }
          grid.appendChild(btn);
        });
      }

      panel.classList.remove('is-letter-mode');
      panel.classList.add('is-size-mode');
    }

    function recalc() {
      var fullCents = 0;
      var variantIds = [];

      slots.forEach(function(slot) {
        var ddl = slot.querySelector('.dl-v7-stack__ddl');
        if (!ddl) return;
        var selectedVariantId = ddl.dataset.selectedVariant || '';
        var selectedPriceCents = parseInt(ddl.dataset.selectedPriceCents || '0', 10);
        var selectedImage = ddl.dataset.selectedImage || '';
        fullCents += selectedPriceCents;
        if (selectedVariantId) variantIds.push(selectedVariantId);
        var imgEl = slot.querySelector('.dl-v7-stack__slot-img');
        if (imgEl && selectedImage) {
          imgEl.style.backgroundImage = 'url(' + selectedImage + ')';
        }
      });

      var bundleCents = Math.round(fullCents * (100 - discountPct) / 100);
      if (priceEl) priceEl.textContent = formatMoney(bundleCents, currencyFormat);
      if (strikeEl) strikeEl.textContent = formatMoney(fullCents, currencyFormat);

      if (ctaEl) {
        if (variantIds.length) {
          ctaEl.setAttribute('href', '/cart/' + variantIds.map(function(v) { return v + ':1'; }).join(','));
        } else {
          ctaEl.setAttribute('href', ctaBaseUrl);
        }
      }
    }

    function wireSlotAddButton(slot, ddl) {
      var addBtn = slot.querySelector('.dl-v7-stack__slot-add');
      if (!addBtn) return;

      var idleText = addBtn.dataset.addText || addBtn.textContent.trim();
      var addedText = addBtn.dataset.addedText || '✓';
      var busyText = addBtn.dataset.busyText || '…';
      var errorText = addBtn.dataset.errorText || 'Fehler';
      var resetTimer = null;

      addBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (addBtn.classList.contains('is-loading')) return;

        var variantId = ddl.dataset.selectedVariant || '';
        if (!variantId) return;

        if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; }
        addBtn.classList.add('is-loading');
        addBtn.disabled = true;
        addBtn.textContent = busyText;

        addVariantToCart(variantId).then(function() {
          addBtn.textContent = addedText;
          resetTimer = setTimeout(function() {
            addBtn.classList.remove('is-loading');
            addBtn.disabled = false;
            addBtn.textContent = idleText;
          }, 1800);
        }).catch(function(err) {
          console.error('[DL v7 Stack] slot add failed', err);
          addBtn.textContent = errorText;
          resetTimer = setTimeout(function() {
            addBtn.classList.remove('is-loading');
            addBtn.disabled = false;
            addBtn.textContent = idleText;
          }, 2200);
        });
      });
    }

    slots.forEach(function(slot) {
      var ddl = slot.querySelector('.dl-v7-stack__ddl');
      if (!ddl) return;
      allDropdowns.push(ddl);

      var btn = ddl.querySelector('.dl-v7-stack__ddl-btn');
      var panel = ddl.querySelector('.dl-v7-stack__ddl-panel');
      var options = ddl.querySelectorAll('.dl-v7-stack__ddl-option');
      var btnThumb = ddl.querySelector('.dl-v7-stack__ddl-thumb');
      var btnLabel = ddl.querySelector('.dl-v7-stack__ddl-label');
      if (panel) allPanels.push(panel);

      if (btn) {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          var willOpen = !ddl.classList.contains('is-open');
          closeAllDropdowns(ddl);
          if (willOpen) ddl.classList.add('is-open');
          else ddl.classList.remove('is-open');
          setSheetOpen(willOpen);
        });
      }

      if (panel) {
        panel.addEventListener('click', function(e) { e.stopPropagation(); });

        /* v18 — explicit close control at the top of the panel: shoppers
           can always collapse the picker this way, regardless of picker
           style, instead of relying only on picking an option or tapping
           outside (the latter is easy to miss on the bottom-sheet style,
           which covers most of the screen). */
        var closeBtn = panel.querySelector('[data-ddl-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            closeVariantMode(panel);
            ddl.classList.remove('is-open');
            setSheetOpen(false);
          });
        }

        var lettersBackBtn = panel.querySelector('[data-ddl-letters-back]');
        if (lettersBackBtn) {
          lettersBackBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            closeVariantMode(panel);
          });
        }

        var sizesBackBtn = panel.querySelector('[data-ddl-sizes-back]');
        if (sizesBackBtn) {
          sizesBackBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            closeVariantMode(panel);
          });
        }
      }

      options.forEach(function(opt) {
        opt.addEventListener('click', function(e) {
          e.preventDefault();

          if (opt.dataset.hasLetters === 'true') {
            openLetterMode(ddl, panel, opt, options, btnThumb, btnLabel);
            return;
          }
          if (opt.dataset.hasLengths === 'true') {
            openSizeMode(ddl, panel, opt, options, btnThumb, btnLabel);
            return;
          }

          options.forEach(function(o) { o.classList.remove('is-selected'); });
          opt.classList.add('is-selected');
          applySelection(ddl, options, btnThumb, btnLabel, opt.dataset.variantId, opt.dataset.priceCents, opt.dataset.image, opt.dataset.name || opt.textContent.trim());
        });
      });

      wireSlotAddButton(slot, ddl);
    });

    if (ctaEl) {
      ctaEl.addEventListener('click', function(e) {
        // Only intercept when this is a real "add the bundle" CTA
        // (i.e. it points at a cart permalink, not a collection fallback).
        var href = ctaEl.getAttribute('href') || '';
        if (href.indexOf('/cart/') !== 0) return;

        e.preventDefault();
        if (ctaEl.classList.contains('is-loading')) return;

        var variantIds = [];
        slots.forEach(function(slot) {
          var ddl = slot.querySelector('.dl-v7-stack__ddl');
          var vid = ddl && ddl.dataset.selectedVariant;
          if (vid) variantIds.push(vid);
        });
        if (!variantIds.length) return;

        var idleHtml = ctaEl.innerHTML;
        ctaEl.classList.add('is-loading');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: variantIds.map(function(id) { return { id: id, quantity: 1 }; }) })
        }).then(function(res) {
          if (!res.ok) {
            return res.json().catch(function() { return {}; }).then(function(err) {
              throw new Error(err.description || err.message || ('HTTP ' + res.status));
            });
          }
          return res.json();
        }).then(function() {
          // Items are in the real cart now — apply the bundle discount
          // code (if configured) before refreshing the drawer, so the
          // drawer's own total reflects the discounted price on first
          // paint instead of updating a second time after the fact.
          return applyDiscountCode();
        }).then(function() {
          document.dispatchEvent(new CustomEvent('opencart', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('updateCart', { bubbles: true }));
          ctaEl.classList.remove('is-loading');
          ctaEl.innerHTML = idleHtml;
        }).catch(function(err) {
          console.error('[DL v7 Stack] bundle add failed', err);
          ctaEl.classList.remove('is-loading');
          ctaEl.innerHTML = idleHtml;
          // Fall back to a normal navigation to the cart permalink so the
          // purchase still completes even if the AJAX call failed.
          window.location.href = href;
        });
      });
    }

    document.addEventListener('click', function() {
      closeAllDropdowns(null);
    });

    recalc();
  }

  function initAll() {
    document.querySelectorAll('.dl-v7-initial').forEach(initInitialPicker);
    document.querySelectorAll('.dl-v7-stack').forEach(initStackBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:select', initAll);
})();
